import { Component, Inject, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  CopilotModelOption,
  CopilotProviderGroup,
  FREE_PROVIDER_SLUG,
  groupModelsByProvider,
  modelLabel,
  providerGroupOf,
  providerLabel,
  providerLogo,
  providerSlugOf,
} from './copilot-model.util';

export interface CopilotModelSelectDialogData {
  models: CopilotModelOption[];
  selectedId: string;
}

/**
 * "Selecionar Modelo IA" dialog — picks a provider (company) and one of its
 * available models from the active LLM gateway catalog (OpenRouter or Vercel).
 * Pricing / credits are intentionally not shown. Closes with the chosen model id
 * (or undefined on cancel).
 */
@Component({
  selector: 'copilot-model-select-dialog',
  templateUrl: './copilot-model-select-dialog.component.html',
  styleUrls: ['./copilot-model-select-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CopilotModelSelectDialogComponent {
  providers: CopilotProviderGroup[] = [];
  selectedProvider = '';
  selectedModelId = '';

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: CopilotModelSelectDialogData,
    private readonly dialogRef: MatDialogRef<CopilotModelSelectDialogComponent>,
  ) {
    this.providers = groupModelsByProvider(data?.models ?? []);
    this.selectedModelId = data?.selectedId ?? '';

    // A channel that chose a free model opens on the Free group (RF-53).
    const initial = providerGroupOf(this.providers, this.selectedModelId) ?? this.providers[0];
    this.selectedProvider = initial?.slug ?? '';
  }

  /** Provider slugs whose logo asset failed to load — render the placeholder instead. */
  readonly logoFailed = new Set<string>();

  readonly freeSlug = FREE_PROVIDER_SLUG;

  get currentModels(): CopilotModelOption[] {
    return this.providers.find((p) => p.slug === this.selectedProvider)?.models ?? [];
  }

  get freeSelected(): boolean {
    return this.selectedProvider === FREE_PROVIDER_SLUG;
  }

  label(model: CopilotModelOption): string {
    return modelLabel(model);
  }

  /** The company that created a free model, since the Free group mixes creators. */
  creator(model: CopilotModelOption): string {
    return providerLabel(providerSlugOf(model.id));
  }

  logo(slug: string): string {
    return providerLogo(slug);
  }

  onLogoError(slug: string): void {
    this.logoFailed.add(slug);
  }

  selectProvider(slug: string): void {
    this.selectedProvider = slug;
  }

  selectModel(id: string): void {
    this.selectedModelId = id;
  }

  cancel(): void {
    this.dialogRef.close();
  }

  confirm(): void {
    if (!this.selectedModelId) return;
    this.dialogRef.close(this.selectedModelId);
  }
}
